import { getFirebaseAuthToken } from '../../utils/firebaseAuthToken';

const AI_GUIDE_CONTENT_URL = process.env.REACT_APP_AI_GUIDE_CONTENT_URL
  || 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendAiGuideContentProducer';

const request = async (payload) => {
  const token = await getFirebaseAuthToken();
  let response;
  try {
    response = await fetch(AI_GUIDE_CONTENT_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (networkError) {
    const error = new Error(
      'Cannot reach the AI Guide Content backend. Deploy backendAiGuideContentProducer and try again.',
    );
    error.code = 'AI_GUIDE_CONTENT_NETWORK_ERROR';
    error.cause = networkError;
    throw error;
  }

  const rawBody = await response.text();
  let body = null;
  try {
    body = rawBody ? JSON.parse(rawBody) : null;
  } catch {
    body = null;
  }

  if (!response.ok) {
    const error = new Error(body?.error || `AI Guide Content request failed (${response.status})`);
    error.status = response.status;
    error.code = body?.code || 'AI_GUIDE_CONTENT_REQUEST_FAILED';
    error.validationErrors = Array.isArray(body?.validationErrors)
      ? body.validationErrors
      : [];
    error.retryAfterSeconds = Number(
      body?.retryAfterSeconds || response.headers.get('Retry-After'),
    ) || null;
    throw error;
  }

  if (!body?.success) {
    throw new Error(body?.error || 'AI Guide Content operation did not complete.');
  }

  return body;
};

const compactPackageForResearch = (packageData) => ({
  manifest: packageData?.manifest || {},
  sources: (packageData?.sources || []).map((source) => ({
    source_id: source.source_id,
    title: source.title,
    reference: source.reference,
  })),
  topics: (packageData?.topics || []).map((topic) => ({
    topic_id: topic.topic_id,
    label: topic.label,
  })),
  claims: (packageData?.claims || []).map((claim) => ({
    claim_id: claim.claim_id,
    text: claim.text,
    topic_ids: claim.topic_ids,
    stop_ids: claim.stop_ids,
  })),
  story_opportunities: (packageData?.story_opportunities || []).map((opportunity) => ({
    opportunity_id: opportunity.opportunity_id,
    discovery_title: opportunity.discovery_title,
    central_question: opportunity.central_question,
    experience_contribution: opportunity.experience_contribution,
    evidence_roles: opportunity.evidence_roles,
    personalization_mode: opportunity.personalization_mode,
    visitor_lenses: opportunity.visitor_lenses,
  })),
  story_promises: (packageData?.story_promises || []).map((promise) => ({
    promise_id: promise.promise_id,
    label: promise.label,
    opportunity_ids: promise.opportunity_ids,
  })),
  guide_shadow: (packageData?.guide_shadow || []).map((shadow) => ({
    stop_id: shadow.stop_id,
    semantic_summary: shadow.semantic_summary,
    claim_refs: shadow.claim_refs,
    anecdotes: shadow.anecdotes,
    arguments: shadow.arguments,
    main_reveal: shadow.main_reveal,
    avoid_repeating: shadow.avoid_repeating,
  })),
});

const guideSnapshot = (itinerary) => ({
  metadata: {
    id: itinerary?.metadata?.id,
    name: itinerary?.metadata?.name,
    city: itinerary?.metadata?.city,
    country: itinerary?.metadata?.country,
    language: itinerary?.metadata?.language,
    description: itinerary?.metadata?.description,
    theme: itinerary?.metadata?.theme,
  },
  points: (itinerary?.points || []).map((point) => ({
    id: point.id,
    name: point.name,
    inDepthDescription: point.inDepthDescription,
    description: point.description,
    coordinates: point.coordinates,
    point_type: point.point_type || point.pointType || point.type,
  })),
});

const canonicalGuideId = (itinerary) => (
  itinerary?.metadata?.parent_guide_id
  || itinerary?.parent_guide_id
  || itinerary?.metadata?.id
  || itinerary?.id
);

const research = ({
  itinerary,
  packageData,
  researchBrief,
  allowedDomains,
  maxSources,
  maxClaims,
}) => request({
  action: 'research',
  guideSnapshot: guideSnapshot(itinerary),
  existingPackage: compactPackageForResearch(packageData),
  researchBrief,
  allowedDomains,
  maxSources,
  maxClaims,
});

const save = ({ itinerary, packageData }) => request({
  action: 'save',
  guideId: packageData?.manifest?.guide_id || canonicalGuideId(itinerary),
  language: packageData?.manifest?.language || itinerary?.metadata?.language,
  country: itinerary?.metadata?.country,
  itineraryName: itinerary?.metadata?.name,
  packageData,
});

const publish = ({ itinerary, packageData }) => request({
  action: 'publish',
  guideId: packageData?.manifest?.guide_id || canonicalGuideId(itinerary),
  language: packageData?.manifest?.language || itinerary?.metadata?.language,
  country: itinerary?.metadata?.country,
  itineraryName: itinerary?.metadata?.name,
  packageData,
});

const load = ({ itinerary }) => request({
  action: 'load',
  guideId: canonicalGuideId(itinerary),
  language: itinerary?.metadata?.language || 'en',
});

export const aiGuideContentService = {
  research,
  save,
  publish,
  load,
};

export default aiGuideContentService;
